import { useParams } from "@tanstack/react-router"
import {
  FALLBACK_LOCALE,
  type Locale,
  isLocale,
  useT,
} from "@holigral/shared/i18n"
import { invariant } from "@holigral/shared/utils"

/**
 * The current locale, never null. Public pages carry the (validated) locale in
 * the URL path; off those routes (the app, not prefixed) it is the active i18n
 * language, set by the route or the signed-in user.
 *
 * This is the single place i18n.language is narrowed to a Locale — it is always
 * one of ours (we configure no language detector and only changeLanguage to
 * supported values), so a mismatch is a "can't happen" we assert loudly rather
 * than silently coerce.
 */
export default function useLocale(): Locale {
  // useT (react-i18next) makes this reactive to changeLanguage.
  const { i18n } = useT()
  // Params are typed by each app's route tree; to shared code they're unknown
  const params: unknown = useParams({ strict: false })
  const locale =
    typeof params === "object" && params !== null && "locale" in params
      ? params.locale
      : undefined

  if (typeof locale === "string" && isLocale(locale)) {
    return locale
  }

  // Off the locale-prefixed routes the language lives on the i18next
  // singleton: per-browser in the browser, but shared across renders on the
  // server, where it reports whichever page rendered last rather than this
  // one. Every server render reaching here produces a document served to all
  // users alike (the SPA shell and the prerendered app routes), so the default
  // is the only locale it can honestly claim — reading the singleton instead
  // left those as <html lang="es-MX"> around English copy, which is Chrome's
  // cue to offer a translation. The browser sets the real locale on its first
  // render, from the URL or the signed-in user's profile.
  if (typeof window === "undefined") {
    return FALLBACK_LOCALE
  }

  const language = i18n.language
  invariant(isLocale(language), `Unsupported i18n language: ${language}`)

  return language
}
